/**
 * Phase 4 Board Demo + Downloads page
 *
 * Public, no auth required. Single-screen executive-facing view that loads
 * live metrics from /api/phase4/summary + /api/phase4/acceptance and exposes
 * one-click downloads for every Phase 4 artifact plus the ZIP packages and
 * the combined Board Binder PDF.
 *
 * Used by Avaganz reviewers, buyer demos, and board review.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Download, FileText, FileJson, FileSpreadsheet, Package,
  ShieldCheck, Clock, CheckCircle2, AlertCircle, ExternalLink,
  Star, BookOpen, FileType, ArrowLeft, LogOut, KeyRound, Eye,
} from 'lucide-react';
import MarkdownPreviewModal from '../components/MarkdownPreviewModal';
import MagicLinkWidget from '../components/MagicLinkWidget';

const formatBytes = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const iconFor = (fmt) => {
  switch (fmt) {
    case 'pdf': return <FileType className="w-4 h-4" />;
    case 'md': return <FileText className="w-4 h-4" />;
    case 'json': return <FileJson className="w-4 h-4" />;
    case 'csv': return <FileSpreadsheet className="w-4 h-4" />;
    case 'zip': return <Package className="w-4 h-4" />;
    default: return <FileText className="w-4 h-4" />;
  }
};

const Stat = ({ label, value, sub, tone }) => (
  <div
    className="liquid-card !p-4"
    data-testid={`stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
  >
    <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-[var(--ink-tertiary)]">{label}</div>
    <div
      className="text-3xl font-bold mt-1"
      style={{ fontFamily: 'Outfit, sans-serif', color: tone || 'var(--ink-primary)' }}
    >
      {value}
    </div>
    {sub && <div className="text-xs text-[var(--ink-tertiary)] mt-1">{sub}</div>}
  </div>
);

/**
 * ReportCard — a single report row with description, PDF download (primary)
 * and Markdown download (secondary, when available).
 */
const ReportCard = ({ item, items, badge, onPreview }) => {
  const mdSibling = item.pairs_with ? items.find((i) => i.name === item.pairs_with) : null;
  // The item to preview inline: if this card is the MD itself, preview it
  // directly; otherwise preview the MD sibling. PDFs/CSVs/JSON have no
  // inline preview path (browsers handle PDF natively via Open PDF).
  const previewable = item.format === 'md' ? item : mdSibling;
  return (
    <div
      className="liquid-card liquid-lift !p-4"
      data-testid={`report-card-${item.name}`}
    >
      <div className="flex items-start gap-3">
        <span className="shrink-0 w-9 h-9 rounded-lg bg-[var(--glass-fill-card)] grid place-items-center text-[var(--ink-secondary)]">
          {iconFor(item.format)}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-[var(--ink-primary)]">{item.label}</span>
            {badge && (
              <span className="text-[9px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded bg-[var(--accent-cyan)]/15 text-[var(--accent-cyan)]">
                {badge}
              </span>
            )}
          </div>
          {item.description && (
            <p className="text-xs text-[var(--ink-secondary)] mt-1 leading-relaxed">
              {item.description}
            </p>
          )}
          <div className="text-[10px] text-[var(--ink-tertiary)] font-mono mt-1">
            {item.name} · {formatBytes(item.size_bytes)}
          </div>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        {previewable && onPreview && (
          <button
            type="button"
            onClick={() => onPreview(previewable)}
            className="liquid-btn liquid-btn--primary text-xs"
            data-testid={`view-inline-${item.name}`}
          >
            <Eye className="w-3 h-3 mr-1.5 inline" />
            View inline
          </button>
        )}
        <a
          href={item.url}
          className={`liquid-btn text-xs ${previewable && onPreview ? '' : 'liquid-btn--primary'}`}
          data-testid={`download-pdf-${item.name}`}
        >
          <Download className="w-3 h-3 mr-1.5 inline" />
          Download {item.format.toUpperCase()}
        </a>
        {mdSibling && (
          <a
            href={mdSibling.url}
            className="liquid-btn text-xs"
            data-testid={`download-md-${mdSibling.name}`}
          >
            <FileText className="w-3 h-3 mr-1.5 inline" />
            Markdown
          </a>
        )}
      </div>
    </div>
  );
};

const Section = ({ title, subtitle, items, parentItems, testid, badge, onPreview }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-8" data-testid={testid}>
      <div className="flex items-baseline gap-3 flex-wrap">
        <h2 className="text-lg font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h2>
        {subtitle && (
          <span className="text-xs text-[var(--ink-tertiary)]">{subtitle}</span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
        {items.map((it) => (
          <ReportCard key={it.name} item={it} items={parentItems} badge={badge} onPreview={onPreview} />
        ))}
      </div>
    </div>
  );
};

export default function Phase4Page() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [acceptance, setAcceptance] = useState(null);
  const [manifest, setManifest] = useState(null);
  const [error, setError] = useState(null);
  const [me, setMe] = useState(null); // {authenticated, role, label, allowed_groups}
  const [previewFile, setPreviewFile] = useState(null);
  const magicWelcome = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('welcome') === 'magic';

  useEffect(() => {
    (async () => {
      try {
        // First check access — if not authenticated, redirect to landing
        const meResp = await fetch('/api/phase4/access/me', { credentials: 'include' }).then((r) => r.json());
        setMe(meResp);
        if (!meResp.authenticated) {
          // Push them back so they can use the Access Report Package CTA
          navigate('/?access=required', { replace: true });
          return;
        }
        const [s, a, m] = await Promise.all([
          fetch('/api/phase4/summary').then((r) => r.json()),
          fetch('/api/phase4/acceptance').then((r) => r.json()),
          fetch('/api/phase4/manifest', { credentials: 'include' }).then((r) => r.json()),
        ]);
        setSummary(s);
        setAcceptance(a);
        setManifest(m);
      } catch (e) {
        setError(String(e));
      }
    })();
  }, [navigate]);

  const handleSignOut = async () => {
    try {
      await fetch('/api/phase4/access/logout', { method: 'POST', credentials: 'include' });
    } catch (e) { /* noop */ }
    navigate('/', { replace: true });
  };

  if (error) {
    return (
      <div className="min-h-screen liquid-shell p-8">
        <div className="liquid-card !p-6 max-w-2xl mx-auto" data-testid="phase4-error">
          <AlertCircle className="w-6 h-6 text-[var(--status-danger-text)]" />
          <h2 className="text-lg font-semibold mt-2">Could not load Phase 4 data</h2>
          <p className="text-sm text-[var(--ink-secondary)] mt-1">{error}</p>
        </div>
      </div>
    );
  }

  if (!summary || !manifest) {
    return (
      <div className="min-h-screen liquid-shell grid place-items-center" data-testid="phase4-loading">
        <div className="liquid-card !p-6 text-sm text-[var(--ink-secondary)]">Loading Phase 4 package…</div>
      </div>
    );
  }

  const ReviewerNav = () => (
    <div
      className="sticky top-0 z-30 backdrop-blur-md bg-black/30 border-b border-white/5"
      data-testid="reviewer-nav"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-3 flex-wrap">
        <button
          onClick={() => navigate('/')}
          className="liquid-pill text-xs hover:text-[var(--accent-cyan)] transition-colors"
          data-testid="back-to-landing"
        >
          <ArrowLeft className="w-3 h-3 mr-1.5 inline" />
          Back to landing
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          {me?.label && (
            <span className="liquid-pill liquid-pill--info text-[10px]" data-testid="reviewer-role-badge">
              <KeyRound className="w-3 h-3 mr-1.5 inline" />
              Signed in as {me.label}
            </span>
          )}
          {me?.role === 'admin' && (
            <Link to="/admin" className="liquid-pill text-xs" data-testid="admin-nav-link">
              <ExternalLink className="w-3 h-3 mr-1.5 inline" />
              Admin console
            </Link>
          )}
          <button
            onClick={handleSignOut}
            className="liquid-pill text-xs hover:text-[var(--status-danger-text)] transition-colors"
            data-testid="reviewer-sign-out"
          >
            <LogOut className="w-3 h-3 mr-1.5 inline" />
            Sign out
          </button>
        </div>
      </div>
    </div>
  );

  const lat = summary.latency_ms || {};
  const items = manifest.items || [];

  // Helpers
  const byName = (name) => items.find((i) => i.name === name);
  // Order PDF items by `order` then by label
  const sortPdfs = (arr) => arr.slice().sort((a, b) => (a.order ?? 999) - (b.order ?? 999) || a.label.localeCompare(b.label));

  const boardPackItems = sortPdfs(items.filter((i) => i.board_pack));
  const primaryPdfs = sortPdfs(items.filter((i) => i.group === 'primary' && i.format === 'pdf'));
  const deepDivePdfs = sortPdfs(items.filter((i) => i.group === 'deep_dive' && i.format === 'pdf'));
  const evidenceItems = items.filter((i) => i.group === 'evidence');
  const bundleItems = sortPdfs(items.filter((i) => i.group === 'bundle'));
  // Split the `verification` group into two visual buckets:
  //   • walkthroughItems — Avaganz session support materials
  //   • verificationItems — machine-readable reviewer kit (Postman, SHA manifest, quick_verify)
  const allVerification = items.filter((i) => i.group === 'verification');
  const walkthroughItems = sortPdfs(allVerification.filter((i) => i.name.includes('WALKTHROUGH') || i.name.startsWith('phase4_walkthrough_')));
  const verificationItems = sortPdfs(allVerification.filter((i) => !(i.name.includes('WALKTHROUGH') || i.name.startsWith('phase4_walkthrough_'))));

  const zipItem = byName('MSML_HEALTH_PHASE4_BOARD_READY_PACKAGE.zip');
  const pdfZipItem = byName('MSML_HEALTH_PHASE4_PDF_REPORTS_ONLY.zip');
  const binderItem = byName('MSML_HEALTH_PHASE4_BOARD_BINDER.pdf');
  const pdfItem = byName('PHASE4_FINAL_PILOT_REPORT.pdf');
  const evidenceItem = byName('phase4_evidence.json');
  const caseMatrixItem = byName('phase4_case_matrix.csv');
  const verificationPdf = byName('PHASE4_RUNTIME_VERIFICATION_PACKAGE.pdf');

  const acceptanceLabel = acceptance?.acceptance_label || 'CONDITIONALLY READY';
  const acceptanceOk = acceptanceLabel === 'ACCEPTANCE READY';
  const isBuyer = me?.role === 'buyer';
  // Reviewer & Admin see the full deep-dive + primary + evidence breakdown.
  // Board-pack + Buyer roles see a curated single board-pack section + bundles.
  const isCuratedView = !!manifest?.board_pack_only;

  return (
    <div className="min-h-screen liquid-shell" data-testid="phase4-page">
      <ReviewerNav />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6">
        {/* Hero */}
        <div className="liquid-card-hero !p-6 sm:!p-8" data-testid="phase4-hero">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-mono text-[var(--accent-cyan)]">
                MSML Health · Phase 4 · Board-Ready Package
              </div>
              <h1
                className="mt-2 text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05]"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Phase 4 Pilot —<br />
                <span
                  className="bg-clip-text"
                  style={{
                    background: 'linear-gradient(135deg, #2ce1e9, #06b6a4)',
                    WebkitBackgroundClip: 'text',
                    backgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    color: 'transparent',
                  }}
                >
                  {acceptanceLabel}
                </span>
              </h1>
              <p className="mt-3 text-base text-[var(--ink-secondary)] max-w-2xl leading-relaxed">
                <strong>Conditionally Ready</strong> means Phase 4 was completed with full
                safety / no-degradation discipline and board-ready evidence, while optimizer
                speed and the absolute-final 95% intent target remain future optimization
                items. Suitable for Avaganz board-level assessment and buyer demos.
              </p>
            </div>
          </div>

          <div className="mt-5 flex items-center gap-3 flex-wrap">
            <span className={`liquid-pill ${acceptanceOk ? 'liquid-pill--success' : 'liquid-pill--warning'}`}>
              <ShieldCheck className="w-3 h-3 mr-1.5 inline" />
              {acceptanceLabel}
            </span>
            <span className="liquid-pill liquid-pill--info">
              <CheckCircle2 className="w-3 h-3 mr-1.5 inline" />
              0 delivered degradations
            </span>
            <span className="liquid-pill liquid-pill--info">
              <CheckCircle2 className="w-3 h-3 mr-1.5 inline" />
              0 safety regressions
            </span>
            <span className="liquid-pill">
              <Clock className="w-3 h-3 mr-1.5 inline" />
              Validation run: {summary.generated_at ? new Date(summary.generated_at).toLocaleString() : '—'}
            </span>
            <Link
              to="/workspace"
              className="liquid-pill text-xs hover:text-[var(--accent-cyan)] transition-colors"
              data-testid="cta-workspace"
            >
              <ExternalLink className="w-3 h-3 mr-1.5 inline" />
              {isBuyer ? 'Try the live product workspace' : 'Open product workspace'}
            </Link>
            {magicWelcome && (
              <span
                className="liquid-pill liquid-pill--success text-xs"
                data-testid="magic-welcome-pill"
              >
                <KeyRound className="w-3 h-3 mr-1.5 inline" />
                Signed in via magic link
              </span>
            )}
          </div>

          {/* Magic-link widget — only admin & reviewer see this */}
          <div className="mt-6">
            <MagicLinkWidget me={me} />
          </div>

          {/* Runtime Verification Package — hero CTA for Avaganz reviewers */}
          {verificationPdf && (
            <div
              className="mt-6 liquid-card !p-4 border border-[var(--accent-cyan)]/25 bg-gradient-to-br from-[var(--accent-cyan)]/8 to-transparent"
              data-testid="rv-package-callout"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <KeyRound className="w-4 h-4 text-[var(--accent-cyan)]" />
                    <span className="text-[10px] uppercase tracking-[0.18em] font-mono text-[var(--accent-cyan)]">
                      Avaganz Evaluation Committee
                    </span>
                  </div>
                  <div
                    className="text-base sm:text-lg font-semibold mt-1"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    Runtime Verification Package
                  </div>
                  <p className="text-xs sm:text-sm text-[var(--ink-secondary)] mt-1 max-w-2xl">
                    Independent reviewer kit — access credentials, URLs, curl examples,
                    expected result ranges, artifact SHA-256 manifest, and a 60–90 minute
                    walkthrough proposal. No fresh 60-case run; 2026-06-03 evidence remains
                    the controlled reference dataset.
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <a
                    href={verificationPdf.url}
                    className="liquid-btn liquid-btn--primary text-xs"
                    data-testid="cta-rv-pdf"
                  >
                    <Download className="w-3 h-3 mr-1.5 inline" />
                    Download PDF
                  </a>
                  <a
                    href={`/api/phase4/view/${encodeURIComponent(verificationPdf.name)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="liquid-btn text-xs"
                    data-testid="cta-rv-open"
                  >
                    <ExternalLink className="w-3 h-3 mr-1.5 inline" />
                    Open in browser
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Top-level CTAs */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2" data-testid="phase4-top-ctas">
            {zipItem && (
              <a href={zipItem.url} className="liquid-btn liquid-btn--primary" data-testid="cta-zip-complete">
                <Package className="w-4 h-4 mr-2 inline" />
                <span className="text-left">
                  Complete ZIP
                  <span className="block text-[9px] opacity-70 font-mono">{formatBytes(zipItem.size_bytes)}</span>
                </span>
              </a>
            )}
            {pdfZipItem && (
              <a href={pdfZipItem.url} className="liquid-btn" data-testid="cta-zip-pdf">
                <Package className="w-4 h-4 mr-2 inline" />
                <span className="text-left">
                  All PDFs ZIP
                  <span className="block text-[9px] opacity-70 font-mono">{formatBytes(pdfZipItem.size_bytes)}</span>
                </span>
              </a>
            )}
            {binderItem && (
              <a href={binderItem.url} className="liquid-btn" data-testid="cta-binder">
                <BookOpen className="w-4 h-4 mr-2 inline" />
                <span className="text-left">
                  Board Binder
                  <span className="block text-[9px] opacity-70 font-mono">{formatBytes(binderItem.size_bytes)} · single PDF</span>
                </span>
              </a>
            )}
            {pdfItem && (
              <a href={pdfItem.url} className="liquid-btn" data-testid="cta-final-pdf">
                <FileType className="w-4 h-4 mr-2 inline" />
                <span className="text-left">
                  Final Report PDF
                  <span className="block text-[9px] opacity-70 font-mono">{formatBytes(pdfItem.size_bytes)}</span>
                </span>
              </a>
            )}
            <div className="flex gap-2">
              {evidenceItem && (
                <a href={evidenceItem.url} className="liquid-btn flex-1" data-testid="cta-evidence-json">
                  <FileJson className="w-4 h-4 mr-1 inline" />
                  <span className="text-[10px]">Evidence JSON</span>
                </a>
              )}
              {caseMatrixItem && (
                <a href={caseMatrixItem.url} className="liquid-btn flex-1" data-testid="cta-case-matrix">
                  <FileSpreadsheet className="w-4 h-4 mr-1 inline" />
                  <span className="text-[10px]">Case Matrix CSV</span>
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Headline metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="phase4-metrics">
          <Stat label="Dataset" value={summary.batch_size} sub="English-only cases" />
          <Stat
            label="Pass rate"
            value={`${summary.pass_rate_pct}%`}
            sub="rule-derived"
            tone={summary.pass_rate_pct >= 95 ? 'var(--status-success-text)' : 'var(--ink-primary)'}
          />
          <Stat
            label="Intent preserved"
            value={`${summary.intent_preservation_pct}%`}
            sub="3-signal classifier"
            tone={summary.intent_preservation_pct >= 95 ? 'var(--status-success-text)' : 'var(--ink-primary)'}
          />
          <Stat
            label="Mean HQ Δ"
            value={`+${summary.mean_hq_delta || ''}`}
            sub={summary.mean_hq_delta ? 'avg improvement' : ''}
          />
          <Stat label="Optimize P50" value={`${(lat.p50 / 1000).toFixed(1)}s`} sub="endpoint" />
          <Stat label="Optimize P95" value={`${(lat.p95 / 1000).toFixed(1)}s`} sub="endpoint" />
          <Stat
            label="Hard 45s breaches"
            value={`${lat.ceiling_breaches ?? 0}`}
            sub={`${summary.timed_out_count ?? 0} graceful timeouts`}
          />
          <Stat
            label="Returned-original"
            value={summary.returned_original_count ?? 0}
            sub="all safe fallbacks"
          />
        </div>

        {/* Runtime signature small card */}
        <div className="liquid-card !p-4 text-xs text-[var(--ink-secondary)]" data-testid="runtime-signature">
          <span className="text-[10px] uppercase tracking-[0.18em] font-mono text-[var(--ink-tertiary)]">
            Runtime signature
          </span>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2 font-mono">
            <div>Scorer / Optimizer / Judge: <strong>claude-sonnet-4-5</strong></div>
            <div>Decoding: <strong>temp=0, top_p=1, seed=42</strong></div>
            <div>Hard timeout: <strong>38.0s</strong> (45s SLA - 7s headroom)</div>
          </div>
        </div>

        {/* Board Pack (recommended primary files) */}
        <div className="liquid-card-hero !p-6" data-testid="board-pack-section">
          <div className="flex items-center gap-3">
            <Star className="w-5 h-5 text-[var(--accent-cyan)]" />
            <h2 className="text-xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {isCuratedView
                ? `Your ${me?.label || 'reviewer'} document pack`
                : 'Board Pack — recommended for Avaganz review'}
            </h2>
          </div>
          <p className="text-sm text-[var(--ink-secondary)] mt-2 max-w-3xl">
            {isCuratedView
              ? 'These are the documents your access role permits. Use the bundles below for one-click distribution.'
              : 'The shortest read-path for board members, transaction reviewers, and Avaganz decision-makers. Start at the top.'}
          </p>
          <Section
            title=""
            items={boardPackItems}
            parentItems={items}
            testid="board-pack-grid"
            badge={isCuratedView ? null : 'Board Pack'}
            onPreview={setPreviewFile}
          />
        </div>

        {/* Primary deliverables — full view only (board-pack/buyer get the curated Board Pack above) */}
        {!isCuratedView && (
          <Section
            title="Primary deliverables"
            subtitle="Headline reports for the package"
            items={primaryPdfs}
            parentItems={items}
            testid="section-primary"
            onPreview={setPreviewFile}
          />
        )}

        {/* Deep-dive briefs — full view only */}
        {!isCuratedView && (
          <Section
            title="Deep-dive briefs"
            subtitle="Technical reports supporting the headline findings"
            items={deepDivePdfs}
            parentItems={items}
            testid="section-deep-dive"
            onPreview={setPreviewFile}
          />
        )}

        {/* Bundles — always shown (role-agnostic distributables) */}
        <Section
          title="Bundles"
          subtitle="Multi-file downloads for distribution"
          items={bundleItems}
          parentItems={items}
          testid="section-bundles"
          onPreview={setPreviewFile}
        />

        {/* Walkthrough kit — Avaganz Runtime Verification session support materials */}
        {walkthroughItems.length > 0 && (
          <div className="mt-8" data-testid="section-walkthrough">
            <div className="flex items-baseline gap-3 flex-wrap">
              <h2 className="text-lg font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Avaganz Walkthrough kit
              </h2>
              <span className="text-xs text-[var(--ink-tertiary)]">
                60–90 min session support — deployment report, facilitator guide, live-tests worksheet, audit packet template, invite email
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
              {walkthroughItems.map((it) => (
                <ReportCard key={it.name} item={it} items={items} onPreview={setPreviewFile} />
              ))}
            </div>
          </div>
        )}

        {/* Runtime Verification files — machine-readable reviewer kit */}
        {verificationItems.length > 0 && (
          <div className="mt-8" data-testid="section-verification">
            <div className="flex items-baseline gap-3 flex-wrap">
              <h2 className="text-lg font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Runtime Verification files
              </h2>
              <span className="text-xs text-[var(--ink-tertiary)]">
                Machine-readable reviewer kit — SHA manifest, Postman collection, quick-verify script
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
              {verificationItems.map((it) => (
                <ReportCard key={it.name} item={it} items={items} onPreview={setPreviewFile} />
              ))}
            </div>
          </div>
        )}

        {/* Raw evidence — hidden for buyer and board_pack roles */}
        {!isCuratedView && !isBuyer && evidenceItems.length > 0 && (
          <div className="liquid-card !p-6" data-testid="section-evidence">
            <h2 className="text-lg font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Raw evidence
            </h2>
            <p className="text-sm text-[var(--ink-secondary)] mt-1">
              Machine-readable inputs for independent replication and review.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
              {evidenceItems.map((it) => (
                <ReportCard key={it.name} item={it} items={items} onPreview={setPreviewFile} />
              ))}
            </div>
          </div>
        )}

        <div className="text-center text-xs text-[var(--ink-tertiary)] py-6" data-testid="phase4-footer">
          Phase 4 package · {manifest.total_count ?? items.length} artifacts · {formatBytes(manifest.total_size_bytes ?? 0)} on disk · Generated {summary.generated_at ? new Date(summary.generated_at).toLocaleString() : '—'}
        </div>
      </div>

      <MarkdownPreviewModal
        open={!!previewFile}
        item={previewFile}
        onClose={() => setPreviewFile(null)}
      />
    </div>
  );
}
